import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';

import CallexLoader from '../../../components/CallexLoader';

import {
  callexAiAgentCalendarDisconnect,
  callexAiAgentCalendarOauthUrlGet,
  callexAiAgentCalendarStatusGet
} from '../../../store/slices/tenantSlices/configurationSlice';

const CallexCalendarModal = ({ isOpen, toggle, agent, onStatusChange }) => {
  const dispatch = useDispatch();
  const popupRef = useRef(null);

  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // Every fresh status also goes to the listing, so its calendar icon stays in step.
  const fetchStatus = async () => {
    if (!agent?.id) return;
    setIsLoading(true);
    try {
      const response = await dispatch(callexAiAgentCalendarStatusGet(agent.id)).unwrap();
      setStatus(response?.data || null);
      onStatusChange?.(agent.id, response?.data || null);
    } catch (error) {
      console.log('error', error);
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && agent?.id) {
      setIsConnecting(false);
      fetchStatus();
    }
  }, [isOpen, agent?.id]);

  // Google redirects to the bot service, which saves the connection and closes the popup
  // itself. Closing it hands focus back to this window, so the status is re-checked then.
  useEffect(() => {
    if (!isConnecting) return undefined;
    const handleFocus = () => {
      if (popupRef.current && !popupRef.current.closed) return;
      popupRef.current = null;
      setIsConnecting(false);
      fetchStatus();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isConnecting]);

  // The popup is opened blank inside the click and pointed at Google once the URL
  // arrives; opening it after the request would be blocked as an unprompted popup.
  const handleConnect = async () => {
    const popup = window.open('', 'callex-google-calendar', 'width=520,height=640');
    if (!popup) {
      toast.error('Popup blocked. Please allow popups for this site and try again.');
      return;
    }
    popupRef.current = popup;
    try {
      const response = await dispatch(callexAiAgentCalendarOauthUrlGet(agent.id)).unwrap();
      if (!response?.data?.url) {
        toast.error('Could not start Google sign-in. Please try again.');
        popup.close();
        return;
      }
      popup.location.href = response.data.url;
      setIsConnecting(true);
    } catch (error) {
      console.log('error', error);
      popup.close();
    }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      const response = await dispatch(callexAiAgentCalendarDisconnect(agent.id)).unwrap();
      if (response?.success) {
        toast.success(response.message || 'Google Calendar disconnected');
        fetchStatus();
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const isConnected = status?.connected === true;

  const getDescription = () => {
    if (isConnected) return status?.email || '-';
    if (isConnecting) {
      return 'Finish the Google sign-in in the popup window. The status updates when the popup closes.';
    }
    return 'Connect a Google Calendar so this agent can book appointments during calls.';
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle} className="py-2">
        <div className="fs-5">Google Calendar{agent?.name ? ` — ${agent.name}` : ''}</div>
      </ModalHeader>
      <ModalBody className="px-4 py-4">
        {isLoading ? (
          <div
            className="p-0 d-flex justify-content-center align-items-center m-0"
            style={{ height: '72px' }}>
            <CallexLoader />
          </div>
        ) : (
          <div className="d-flex align-items-center gap-3 p-3 rounded border">
            <div
              className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${
                isConnected ? 'bg-success-subtle' : 'bg-light'
              }`}
              style={{ width: '48px', height: '48px' }}>
              <Icon.Calendar size={22} className={isConnected ? 'text-success' : 'text-muted'} />
            </div>
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <div
                className={`fw-semibold d-flex align-items-center gap-1 ${
                  isConnected ? 'text-success' : 'text-muted'
                }`}>
                {isConnected ? <Icon.CheckCircle size={16} /> : <Icon.XCircle size={16} />}
                {isConnected ? 'Connected' : 'Not connected'}
              </div>
              <div
                className={`small text-muted ${isConnected ? 'text-truncate' : ''}`}
                title={isConnected ? status?.email : undefined}>
                {getDescription()}
              </div>
            </div>
            <Icon.RefreshCw
              size={16}
              title="Refresh status"
              className="cursor-pointer text-info flex-shrink-0"
              onClick={fetchStatus}
            />
          </div>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-center">
        <Button onClick={toggle} className="px-5 bg-transparent text-primary">
          Close
        </Button>
        {!isLoading &&
          (isConnected ? (
            <Button
              outline
              color="danger"
              className="px-5"
              onClick={handleDisconnect}
              disabled={isDisconnecting}>
              {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          ) : (
            <Button
              className="px-4 bg-primary border-primary"
              onClick={handleConnect}
              disabled={isConnecting}>
              Connect Google Calendar
            </Button>
          ))}
      </ModalFooter>
    </Modal>
  );
};

CallexCalendarModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  agent: PropTypes.object,
  onStatusChange: PropTypes.func
};

export default CallexCalendarModal;
